/**
 * Ringside Banner placement — pure screen-space math, no Phaser, no DOM.
 * Unit-tested in test/duel-banner-layout.test.ts.
 *
 * THE RULE (owner ruling 2026-08-03, "the ui of player and opponent
 * doesn't always line up with the world"): each battler's nameplate is
 * displaced OUTBOARD along the duel axis — away from its opponent — so
 * the banners' order along that axis is always the creatures' order
 * along that axis. Nothing here branches on WHICH battler is the
 * player's; it branches on screen position only, which is precisely
 * why the wild-south and wild-west orientations stop being inverted.
 * There is no case to get backwards because there is no case.
 *
 * The predecessor was two corner-anchored panels (opponent hard-pinned
 * top-right, player bottom-left) inherited from the full-screen battle
 * scene, where the opponent was always up-and-right. Duels run on
 * whatever axis the player engaged from, so half of them read
 * backwards and the south case parked the player's own panel on top of
 * the wild's sprite.
 */

export interface BannerBox {
  w: number;
  h: number;
}

/** Where a battler is on screen, in CSS px (already projected). */
export interface BattlerAnchor {
  /** Horizontal center of the sprite. */
  centerX: number;
  /** Feet (the sprite's origin-bottom anchor). */
  feetY: number;
  /** Top of the visible art — feet minus the head clearance, already
   *  scaled by the camera zoom by the caller's projection. */
  headY: number;
}

export interface BannerPlacement {
  left: number;
  top: number;
  /** Which edge of the banner the stem exits from, pointing at its
   *  own creature. */
  stemSide: 'top' | 'bottom';
  /** Stem offset from the banner's left edge, already clamped to stay
   *  on the banner even after the banner itself was clamped to the
   *  viewport. */
  stemX: number;
}

export interface DuelBannerLayoutParams {
  /** Dominant separation axis between the two battler home tiles.
   *  Ties resolve horizontal, matching findDuelSlot's own convention
   *  so staging and chrome can never disagree about the axis. */
  axis: 'horizontal' | 'vertical';
  a: BattlerAnchor;
  b: BattlerAnchor;
  aBox: BannerBox;
  bBox: BannerBox;
  viewportW: number;
  /** Screen-y band the chrome may occupy: above the battle log's top
   *  edge, below the top screen margin. */
  safeTop: number;
  safeBottom: number;
  /** Clearance between a banner and its creature's art. */
  gap: number;
  /** Minimum distance from the viewport's left/right edges. */
  edge: number;
}

export interface DuelBannerLayout {
  a: BannerPlacement;
  b: BannerPlacement;
}

const STEM_INSET = 12;

function clamp(v: number, lo: number, hi: number): number {
  // lo wins a degenerate range — a banner taller than the safe band
  // should overflow downward (into the log) rather than upward off
  // the top of the screen, where it would be unrecoverable.
  return Math.max(lo, Math.min(hi, v));
}

/** Stem x within the banner, so it keeps pointing at its creature even
 *  after the banner was clamped away from the creature's column. */
function stemFor(centerX: number, left: number, w: number): number {
  return clamp(centerX - left, STEM_INSET, Math.max(STEM_INSET, w - STEM_INSET));
}

export function placeDuelBanners(p: DuelBannerLayoutParams): DuelBannerLayout {
  if (p.axis === 'vertical') {
    // Upper battler's banner goes ABOVE its head, lower battler's goes
    // BELOW its feet. Whoever is higher on screen gets the high
    // banner — the ordering is read off the screen, not off identity.
    const aIsUpper = p.a.feetY <= p.b.feetY;
    const upper = aIsUpper ? p.a : p.b;
    const lower = aIsUpper ? p.b : p.a;
    const upperBox = aIsUpper ? p.aBox : p.bBox;
    const lowerBox = aIsUpper ? p.bBox : p.aBox;

    const upperLeft = clamp(
      Math.round(upper.centerX - upperBox.w / 2),
      p.edge,
      Math.max(p.edge, p.viewportW - p.edge - upperBox.w),
    );
    const upperTop = clamp(
      Math.round(upper.headY - p.gap - upperBox.h),
      p.safeTop,
      Math.max(p.safeTop, p.safeBottom - upperBox.h),
    );
    const lowerLeft = clamp(
      Math.round(lower.centerX - lowerBox.w / 2),
      p.edge,
      Math.max(p.edge, p.viewportW - p.edge - lowerBox.w),
    );
    const lowerTop = clamp(
      Math.round(lower.feetY + p.gap),
      p.safeTop,
      Math.max(p.safeTop, p.safeBottom - lowerBox.h),
    );

    const upperPlacement: BannerPlacement = {
      left: upperLeft,
      top: upperTop,
      stemSide: 'bottom',
      stemX: stemFor(upper.centerX, upperLeft, upperBox.w),
    };
    const lowerPlacement: BannerPlacement = {
      left: lowerLeft,
      top: lowerTop,
      stemSide: 'top',
      stemX: stemFor(lower.centerX, lowerLeft, lowerBox.w),
    };
    return aIsUpper
      ? { a: upperPlacement, b: lowerPlacement }
      : { a: lowerPlacement, b: upperPlacement };
  }

  // HORIZONTAL axis: both battlers share a row, so there is no vertical
  // gutter between them to exploit — and at duel zoom the pair fills
  // most of a phone's width, so there is no horizontal gutter either.
  // Both banners therefore sit in the sky band above the pair, and the
  // ORDER carries the information: left creature's banner docks left,
  // right creature's docks right. Deterministic docking (rather than
  // centering + collision resolution) means they can never cross.
  const aIsLeft = p.a.centerX <= p.b.centerX;
  const leftAnchor = aIsLeft ? p.a : p.b;
  const rightAnchor = aIsLeft ? p.b : p.a;
  const leftBox = aIsLeft ? p.aBox : p.bBox;
  const rightBox = aIsLeft ? p.bBox : p.aBox;

  const bandTop = clamp(
    Math.round(Math.min(p.a.headY, p.b.headY) - p.gap - Math.max(leftBox.h, rightBox.h)),
    p.safeTop,
    Math.max(p.safeTop, p.safeBottom - Math.max(leftBox.h, rightBox.h)),
  );

  const leftLeft = p.edge;
  // Right banner is right-docked; if the two would still overlap on a
  // very narrow viewport, the left one wins its edge and the right one
  // is pushed flush against it — order is preserved either way.
  const rightLeft = Math.max(
    leftLeft + leftBox.w,
    p.viewportW - p.edge - rightBox.w,
  );

  const leftPlacement: BannerPlacement = {
    left: leftLeft,
    top: bandTop,
    stemSide: 'bottom',
    stemX: stemFor(leftAnchor.centerX, leftLeft, leftBox.w),
  };
  const rightPlacement: BannerPlacement = {
    left: rightLeft,
    top: bandTop,
    stemSide: 'bottom',
    stemX: stemFor(rightAnchor.centerX, rightLeft, rightBox.w),
  };
  return aIsLeft
    ? { a: leftPlacement, b: rightPlacement }
    : { a: rightPlacement, b: leftPlacement };
}

/** Dominant-axis test, shared with the staging math: ties prefer
 *  horizontal exactly like findDuelSlot, so a diagonal-ish pair can
 *  never be staged on one axis and captioned on the other. */
export function duelAxis(
  aTile: { x: number; y: number },
  bTile: { x: number; y: number },
): 'horizontal' | 'vertical' {
  return Math.abs(bTile.x - aTile.x) >= Math.abs(bTile.y - aTile.y)
    ? 'horizontal'
    : 'vertical';
}
